import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenant } from "../tenant";
import { useAuth } from "../auth";
import { BrandMark } from "../BrandMark";
import { endpoints } from "../api";
import type { Catalog } from "../types";

/**
 * The start page — where every signed-in person lands, before the dashboard.
 *
 * It has to work for two very different people. Consultant staff live in the
 * app all day and know where everything is. An Owner, a Contractor or a
 * specialist consultant signs in occasionally, from site, and wants one
 * question answered: **what needs me?**
 *
 * So the page leads with that count and then offers three or four large doors,
 * chosen by what the person is actually allowed to do. No menus to learn, no
 * dashboard to interpret. Still one viewport with no scrolling — it is a
 * starting point, not a screen to read.
 */
export function StartPage() {
  const tenant = useTenant();
  const { user, can } = useAuth();
  const { t } = useTranslation();
  const [waiting, setWaiting] = useState<number | null>(null);
  const [roleLabel, setRoleLabel] = useState("");

  const hasProjects =
    tenant.modules.includes("eng.projects") && (can("projects.read") || can("projects.manage"));

  useEffect(() => {
    if (!hasProjects) return;
    // The inbox already answers "what is with me right now" across every
    // project — the number is the most useful thing on this page.
    endpoints
      .myActions()
      .then((a) => setWaiting(a.length))
      .catch(() => setWaiting(null));
  }, [hasProjects]);

  useEffect(() => {
    // Say which hat the person is wearing. An Owner who lands somewhere and is
    // not sure why they can see what they can see is an Owner who stops using it.
    endpoints
      .getCatalog()
      .then((c: Catalog) => {
        const mine = (user?.roles ?? [])[0];
        setRoleLabel(c.roles.find((r) => r.key === mine)?.label ?? "");
      })
      .catch(() => setRoleLabel(""));
  }, [user]);

  const first = (user?.name ?? "").split(" ")[0] || user?.name || "";

  return (
    <div className="start">
      <div className="start__inner">
        <div className="start__mark">
          {tenant.theme.logo ? (
            <img src={tenant.theme.logo} alt="" className="start__logo" />
          ) : (
            <BrandMark icon={tenant.theme.icon} size={76} />
          )}
        </div>

        <h1 className="start__name">{tenant.name}</h1>
        <p className="start__powered">
          <span className="start__brand">AigntX</span>
          <span className="start__dot" />
          {t("start.tagline")}
        </p>

        <p className="start__welcome">
          {t("start.welcome", { name: first })}
          {roleLabel && <span className="start__role">{roleLabel}</span>}
        </p>

        {hasProjects && (
          <Link className={waiting ? "start__waiting is-on" : "start__waiting"} to="/dashboard">
            {waiting === null ? (
              <span className="start__wtext">{t("start.checking")}</span>
            ) : waiting > 0 ? (
              <>
                <span className="start__wcount">{waiting}</span>
                <span className="start__wtext">{t("start.waiting", { count: waiting })}</span>
              </>
            ) : (
              <span className="start__wtext">{t("start.allClear")}</span>
            )}
          </Link>
        )}

        <div className="start__doors">
          {hasProjects && (
            <Door to="/projects" icon="▣" title={t("start.projects")} note={t("start.projectsNote")} />
          )}
          {tenant.modules.includes("crm") && can("records.read") && (
            <Door to="/records" icon="▤" title={t("app.records")} note={t("start.recordsNote")} />
          )}
          <Door to="/data" icon="◫" title={t("app.data")} note={t("start.dataNote")} />
          {(can("users.read") || can("tenant.manage")) && (
            <Door to="/company" icon="⌂" title={t("app.company")} note={t("start.companyNote")} />
          )}
        </div>
      </div>
    </div>
  );
}

function Door({
  to,
  icon,
  title,
  note,
}: {
  to: string;
  icon: string;
  title: string;
  note: string;
}) {
  return (
    <Link className="start__door" to={to}>
      <span className="start__dicon" aria-hidden="true">
        {icon}
      </span>
      <span className="start__dtitle">{title}</span>
      <span className="start__dnote">{note}</span>
    </Link>
  );
}
